import type { ContentSection } from './dashboard-content-search';

/**
 * The dashboard filter panel's Date / Category / Created By, applied to Foodie News or Deals.
 * News categories are its tags and deals are Deal or Offer; Created By is the restaurant.
 */
export interface ContentFilters {
  /** Inclusive local dates, yyyy-mm-dd. */
  from?: string | null;
  to?: string | null;
  categories?: string[];
  /** A restaurant key from contentRestaurant(). */
  restaurant?: string | null;
}

export interface ContentRestaurant {
  key: string;
  name: string;
}

export const DEAL_CATEGORIES = ['Deal', 'Offer'];

export function contentCategories(section: ContentSection, record: any): string[] {
  if (section === 'deals') return [record?.type == 1 ? 'Deal' : 'Offer'];
  return (Array.isArray(record?.tagList) ? record.tagList : []).filter(Boolean);
}

export function contentRestaurant(section: ContentSection, record: any): ContentRestaurant | null {
  if (section === 'deals') {
    const name = String(record?.restaurant ?? '').trim();
    if (!name) return null;
    return { key: record?.vendorId ? `v:${record.vendorId}` : `n:${name.toLocaleLowerCase()}`, name };
  }
  const vendor = record?.vendor;
  if (vendor?.title) return { key: `v:${vendor.id ?? record?.vendorId ?? vendor.title}`, name: vendor.title };
  const branch = record?.promotionBranchMappingsIdTitle?.find((item: any) => item?.title);
  return branch ? { key: `b:${branch.id}`, name: branch.title } : null;
}

/** Local calendar day of an API date, yyyy-mm-dd. */
export function dayKey(value: any): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return [date.getFullYear(), date.getMonth() + 1, date.getDate()].map(pad).join('-');
}

/** The same key for a datepicker value ({ year, month, day }). */
export function dateStructKey(value: { year: number, month: number, day: number } | null | undefined): string | null {
  return value?.year ? [value.year, value.month, value.day].map(pad).join('-') : null;
}

// News is dated by when it was posted; a deal by the days it runs.
function dayRange(section: ContentSection, record: any): [string | null, string | null] {
  if (section === 'news') {
    const posted = dayKey(record?.createdOn);
    return [posted, posted];
  }
  const start = dayKey(record?.dateFrom ?? record?.createdOn);
  return [start, dayKey(record?.dateTo) ?? start];
}

export function filtersMatch(section: ContentSection, record: any, filters?: ContentFilters | null): boolean {
  if (!filters) return true;
  if (filters.categories?.length && !contentCategories(section, record).some(item => filters.categories!.includes(item))) {
    return false;
  }
  if (filters.restaurant && contentRestaurant(section, record)?.key !== filters.restaurant) return false;
  if (filters.from || filters.to) {
    const from = filters.from || filters.to!, to = filters.to || filters.from!;
    const [start, end] = dayRange(section, record);
    if (!start || start > to || (end ?? start) < from) return false;
  }
  return true;
}

export function filtersKey(filters?: ContentFilters | null): string {
  if (!filters) return '';
  return JSON.stringify([filters.from || '', filters.to || '', [...(filters.categories || [])].sort(), filters.restaurant || '']);
}

/** Filter choices offered for a feed, from every record seen so far. */
export function contentFilterOptions(section: ContentSection, records: any[]) {
  const restaurants = new Map<string, ContentRestaurant>();
  const tags = new Set<string>();
  for (const record of records || []) {
    const restaurant = contentRestaurant(section, record);
    if (restaurant && !restaurants.has(restaurant.key)) restaurants.set(restaurant.key, restaurant);
    if (section === 'news') contentCategories(section, record).forEach(tag => tags.add(tag));
  }
  const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
  return {
    categories: section === 'deals' ? [...DEAL_CATEGORIES] : [...tags].sort(byName),
    restaurants: [...restaurants.values()].sort((a, b) => byName(a.name, b.name)),
  };
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}
